'use client';

import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  styled,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { config } from '../../../config';
import { AccountSelectorDropdown, AccountDropdownOption } from './AccountSelectorDropdown';
import { AccountStatusCard } from './AccountStatusCard';
import { AnnualPaymentHint } from './AnnualPaymentHint';
import { getGuestMaxAmount } from './guestPaymentSchema';
import { formatPhone } from '../../../utils/formatHelpers';
import { usePaymentStorage } from '../../../hooks/usePaymentStorage';
import type { PaymentPrefill } from '../../../utils/paymentPrefill';

/**
 * Строит схему валидации формы оплаты авторизованного пользователя.
 *
 * Максимальная сумма: `config.payment.annualMaxAmount` при годовой оплате,
 * иначе `config.payment.maxAmount` (как в форме гостя, см. `getGuestMaxAmount`).
 *
 * @param annual - признак годовой оплаты по акции
 * @returns yup-схема для resolver react-hook-form
 */
export const buildAuthPaymentSchema = (annual: boolean) => {
  const maxAmount = getGuestMaxAmount(annual);
  const { minAmount, currencySymbol } = config.payment;

  return yup.object({
    accountId: yup.string().required('Выберите лицевой счет').min(1, 'Выберите лицевой счет'),
    amount: yup
      .number()
      .typeError('Введите сумму')
      .required('Введите сумму')
      .min(minAmount, `Минимальная сумма ${minAmount} ${currencySymbol}`)
      .max(maxAmount, `Максимальная сумма ${maxAmount} ${currencySymbol}`),
    email: yup
      .string()
      .email('Неверный формат email')
      .notRequired(),
  });
};

/**
 * Тип данных формы оплаты авторизованного пользователя после валидации
 */
export type AuthPaymentFormData = yup.InferType<ReturnType<typeof buildAuthPaymentSchema>>;

/**
 * Значения полей формы оплаты авторизованного пользователя (вход resolver react-hook-form)
 */
interface AuthPaymentFormValues {
  /** ID выбранного лицевого счёта */
  accountId: string;
  /** Сумма платежа, ₽ (`undefined`, пока поле пустое) */
  amount: number;
  /** Email для чека */
  email: string;
}

/**
 * Проверяет, что в поле суммы есть число (пустое поле хранится как NaN или undefined).
 *
 * @param value - значение поля суммы
 * @returns true, если значение — число, не NaN
 */
const hasAmountValue = (value: number | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

/**
 * Данные для отправки на сервер
 */
export interface PaymentDataAuth {
  accountId: string;
  amount: number;
  email?: string;
  /** Признак годовой оплаты по акции (из ссылки Telegram-бота); сервер проверяет сумму сам */
  annual?: boolean;
}

/**
 * Информация о лицевом счете
 */
export interface AccountInfo {
  id: string;
  accountNumber: string;
  phone: string;
  address?: string;
  balance: number;
  paidUntil: Date | string | null;
  tariffCost: number;
  isBlocked: boolean;
}

/**
 * Свойства компонента PaymentFormAuth
 */
export interface PaymentFormAuthProps {
  /** Список лицевых счетов пользователя */
  accounts: AccountInfo[];
  /** ID выбранного лицевого счета */
  selectedAccountId: string;
  /** Обработчик изменения выбранного счета */
  onAccountChange: (accountId: string) => void;
  /** Обработчик отправки формы */
  onSubmit: (data: PaymentDataAuth) => Promise<void>;
  /** Флаг загрузки */
  isLoading?: boolean;
  /** Сообщение об ошибке */
  error?: string | null;
  /** Флаг открытия модального окна */
  open?: boolean;
  /** Обработчик закрытия модального окна */
  onClose?: () => void;
  /** Email пользователя (из аккаунта) */
  userEmail?: string;
  /**
   * Данные предзаполнения из URL (ссылка Telegram-бота): номер ЛС, сумма, признак годовой оплаты.
   * Имеют приоритет над сохранёнными в localStorage данными; сумма остаётся редактируемой.
   */
  prefill?: PaymentPrefill;
}

/**
 * Стилизованное модальное окно
 */
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '480px',
    maxWidth: '90%',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',

    [theme.breakpoints.down('sm')]: {
      width: '90%',
      margin: '16px',
    },
  },
}));

/**
 * Стилизованный заголовок диалога
 */
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: '24px 32px',
  fontSize: '24px',
  fontWeight: 600,
  fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e1e1e',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,

  [theme.breakpoints.down('sm')]: {
    padding: '16px 20px',
    fontSize: '20px',
  },
}));

/**
 * Стилизованный контент диалога
 */
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '32px',
  paddingTop: '24px',

  [theme.breakpoints.down('sm')]: {
    padding: '24px',
    paddingTop: '20px',
  },
}));

/**
 * Контейнер формы
 */
const FormContainer = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',

  [theme.breakpoints.down('sm')]: {
    gap: '20px',
  },
}));

/**
 * Стилизованная кнопка отправки
 */
const SubmitButton = styled(Button)(({ theme }) => ({
  height: '48px',
  textTransform: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',
  backgroundColor: '#1976d2',
  color: '#ffffff',

  '&:hover': {
    backgroundColor: '#1565c0',
  },

  '&:disabled': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
  },

  [theme.breakpoints.down('sm')]: {
    height: '44px',
    fontSize: '15px',
  },
}));

/**
 * Стилизованное текстовое поле
 */
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',

    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
    },

    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },

    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },

    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
  },

  '& .MuiInputLabel-root': {
    fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',

    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
  },

  '& .MuiFormHelperText-root': {
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
    },
  },
}));

/**
 * Контейнер информации о пользователе
 */
const UserInfoContainer = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  marginTop: '24px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,

  [theme.breakpoints.down('sm')]: {
    padding: '10px 14px',
  },
}));

/**
 * Компонент формы оплаты для авторизованных пользователей
 * 
 * Отображает модальное окно с упрощенной формой для авторизованных пользователей.
 * Показывает информацию о пользователе, статус счета, селектор счетов (если несколько)
 * и кнопку быстрой оплаты (если требуется).
 *
 * Предзаполнение из ссылки Telegram-бота (`prefill`): счёт с номером ЛС из ссылки выбирается
 * через `onAccountChange` один раз (последующий ручной выбор не перебивается); если номер не
 * найден среди счетов клиента, показывается предупреждение, оплата не блокируется. Сумма из
 * ссылки подставляется в поле суммы (редактируется), при `annual` показывается подсказка
 * «Оплата за год по акции», лимит суммы повышается, а признак `annual` передаётся в `onSubmit`.
 * 
 * @example
 * ```tsx
 * <PaymentFormAuth
 *   accounts={accounts}
 *   selectedAccountId={selectedId}
 *   onAccountChange={handleAccountChange}
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSubmit={async (data) => {
 *     await generatePayment(data);
 *   }}
 *   isLoading={isGenerating}
 *   error={errorMessage}
 * />
 * ```
 */
export function PaymentFormAuth({
  accounts,
  selectedAccountId,
  onAccountChange,
  onSubmit,
  isLoading = false,
  error = null,
  open = true,
  onClose,
  userEmail,
  prefill,
}: PaymentFormAuthProps) {
  // Находим выбранный счет
  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);

  // Годовая оплата по акции (ссылка Telegram-бота) и соответствующий лимит суммы
  const annual = prefill?.annual === true;
  const maxAmount = getGuestMaxAmount(annual);
  const schema = useMemo(() => buildAuthPaymentSchema(annual), [annual]);

  // Получаем данные из localStorage
  const { data: storageData, updateData, isLoaded } = usePaymentStorage();

  // Флаг для отслеживания, был ли уже установлен начальный email
  const emailInitializedRef = useRef(false);

  // Номер ЛС из ссылки, для которого выбор счёта уже применён (не перебиваем ручной выбор клиента)
  const appliedAccountNumberRef = useRef<string | null>(null);

  const prefillAccountNumber = prefill?.accountNumber;

  // Счёт клиента, соответствующий номеру ЛС из ссылки
  const prefillAccount = prefillAccountNumber
    ? accounts.find((acc) => acc.accountNumber === prefillAccountNumber)
    : undefined;

  // Номер из ссылки не найден среди счетов клиента: подсказываем выбрать нужный счёт вручную
  const prefillNotFound = Boolean(prefillAccountNumber) && accounts.length > 0 && !prefillAccount;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm<AuthPaymentFormValues, undefined, AuthPaymentFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      accountId: selectedAccountId,
      // Сумма из URL (Req 7.2); без неё поле пустое (Req 7.3)
      amount: prefill?.amount,
      email: '',
    },
  });

  // Выбор счёта по номеру ЛС из ссылки: один раз для каждого номера, когда список счетов известен
  useEffect(() => {
    if (!prefillAccountNumber || accounts.length === 0) {
      return;
    }
    if (appliedAccountNumberRef.current === prefillAccountNumber) {
      return;
    }
    appliedAccountNumberRef.current = prefillAccountNumber;
    if (prefillAccount) {
      onAccountChange(prefillAccount.id);
    }
  }, [prefillAccountNumber, prefillAccount, accounts.length, onAccountChange]);

  // Обновляем accountId в форме при изменении выбранного счета
  useEffect(() => {
    setValue('accountId', selectedAccountId);
  }, [selectedAccountId, setValue]);

  // Загрузка email ТОЛЬКО ОДИН РАЗ при монтировании: сначала из аккаунта, потом из localStorage
  useEffect(() => {
    if (isLoaded && !emailInitializedRef.current) {
      const emailToSet = userEmail || storageData?.email || '';
      setValue('email', emailToSet);
      emailInitializedRef.current = true;
    }
  }, [isLoaded, userEmail, storageData?.email, setValue]);

  /**
   * Обработчик отправки формы
   * Мемоизирован для предотвращения создания новой функции при каждом рендере
   */
  const handleFormSubmit = useCallback(async (data: AuthPaymentFormData) => {
    try {
      // Валидация суммы
      // (для годовой оплаты лимит выше; меньше годовой суммы не блокируем — сервер проверит сам)
      if (data.amount < config.payment.minAmount || data.amount > maxAmount) {
        throw new Error(`Сумма должна быть от ${config.payment.minAmount} до ${maxAmount} ${config.payment.currencySymbol}`);
      }

      // Проверка наличия выбранного счета
      if (!selectedAccount) {
        throw new Error('Выберите лицевой счет');
      }

      const paymentData: PaymentDataAuth = {
        accountId: data.accountId,
        amount: data.amount,
        email: data.email || undefined,
        annual,
      };

      await onSubmit(paymentData);
    } catch (err) {
      // Ошибки валидации обрабатываются здесь, остальные - в родительском компоненте
      console.error('Ошибка валидации формы:', err);
      throw err;
    }
  }, [selectedAccount, onSubmit, maxAmount, annual]);

  // Преобразуем accounts в формат для AccountSelectorDropdown
  const accountOptions: AccountDropdownOption[] = accounts.map((acc) => ({
    id: acc.id,
    accountNumber: acc.accountNumber,
    address: acc.address,
    balance: acc.balance,
    isBlocked: acc.isBlocked,
  }));

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogTitle>
        Оплата услуг
        {onClose && (
          <IconButton
            aria-label="закрыть"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </StyledDialogTitle>

      <StyledDialogContent>
        <FormContainer onSubmit={handleSubmit(handleFormSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Информация о пользователе */}
          {selectedAccount && (
            <UserInfoContainer>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '12px',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Номер телефона
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {formatPhone(selectedAccount.phone)}
              </Typography>
            </UserInfoContainer>
          )}

          {/* Номер ЛС из ссылки не найден среди счетов клиента (оплата не блокируется) */}
          {prefillNotFound && (
            <Alert severity="warning">
              Лицевой счёт из ссылки не найден среди ваших счетов. Выберите нужный счёт из списка.
            </Alert>
          )}

          {/* Селектор лицевого счета (если несколько) */}
          {accounts.length > 0 && (
            <AccountSelectorDropdown
              accounts={accountOptions}
              selectedAccountId={selectedAccountId}
              onAccountChange={onAccountChange}
              disabled={isLoading}
            />
          )}

          {/* Карточка статуса счета */}
          {selectedAccount && (
            <AccountStatusCard
              balance={selectedAccount.balance}
              paidUntil={selectedAccount.paidUntil}
              tariffCost={selectedAccount.tariffCost}
              isBlocked={selectedAccount.isBlocked}
            />
          )}

          {/* Подсказка годовой оплаты по акции (только при annual в ссылке бота) */}
          {annual && <AnnualPaymentHint />}

          {/* Поле суммы пополнения */}
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                value={hasAmountValue(field.value) ? field.value : ''}
                label="Сумма пополнения"
                type="number"
                placeholder={hasAmountValue(field.value) ? '' : `${config.payment.minAmount}-${maxAmount}`}
                error={!!errors.amount}
                helperText={
                  errors.amount?.message
                    ? errors.amount.message
                    : `Минимум ${config.payment.minAmount} ${config.payment.currencySymbol}, максимум ${maxAmount} ${config.payment.currencySymbol}`
                }
                fullWidth
                required
                disabled={isLoading}
                onChange={(e) => {
                  // Пустое значение хранится как NaN: yup выдаст «Введите сумму», а react-hook-form
                  // не подставит обратно предзаполненную сумму (undefined откатывается к defaultValue)
                  const value = e.target.value ? Number(e.target.value) : Number.NaN;
                  field.onChange(value);
                }}
                InputLabelProps={{
                  shrink: hasAmountValue(field.value) ? true : undefined,
                }}
                InputProps={{
                  endAdornment: <Box sx={{ ml: 1, color: 'text.secondary' }}>{config.payment.currencySymbol}</Box>,
                }}
              />
            )}
          />

          {/* Поле email */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                label="Email (необязательно)"
                type="email"
                placeholder="example@mail.ru"
                error={!!errors.email}
                helperText={errors.email?.message ? (errors.email.message as string) : 'Для отправки чека об оплате'}
                fullWidth
                onChange={(e) => {
                  field.onChange(e);
                  // Автосохранение в localStorage
                  updateData({ email: e.target.value });
                }}
              />
            )}
          />

          {/* Кнопка отправки */}
          <SubmitButton
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !isValid}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Перейти к оплате'
            )}
          </SubmitButton>
        </FormContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
}
